import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useFormApi } from "../useFormApi.js";
function useAddFormValidation(validateFormFields, options = {}) {
  const { disabled, message, onUnmountBehavior = "clear-errors" } = options;
  const formApi = useFormApi();
  const rOnUnmountBehavior = useRef(onUnmountBehavior);
  useLayoutEffect(() => void (rOnUnmountBehavior.current = onUnmountBehavior), [onUnmountBehavior]);
  const rValidationUID = useRef(`form-${Math.random()}`);
  const rErrorsUIDs = useRef([]);
  useEffect(() => {
    if (disabled) return;
    const validationId = rValidationUID.current;
    function storeEmittedErrors(errors) {
      if (errors) {
        Object.keys(errors).forEach((errorUID) => {
          if (!rErrorsUIDs.current.includes(errorUID)) {
            rErrorsUIDs.current.push(errorUID);
          }
        });
      }
    }
    const validator = createFormFieldsValidation(validationId, validateFormFields, {
      disabled,
      message
    });
    const validatorWrapper = (formApi2) => {
      const errors = validator(formApi2);
      if (errors instanceof Promise) {
        return errors.then((errors2) => {
          storeEmittedErrors(errors2);
          return errors2;
        });
      }
      storeEmittedErrors(errors);
      return errors;
    };
    formApi.addValidation(validationId, validatorWrapper);
    return () => {
      formApi.removeValidation(validationId);
    };
  }, [formApi, disabled, message, validateFormFields]);
  useEffect(() => {
    return () => {
      if (rOnUnmountBehavior.current === "clear-errors") {
        formApi.clearError(...rErrorsUIDs.current);
      }
    };
  }, [formApi]);
  return {
    getErrorsUIDs: useCallback(() => rErrorsUIDs.current, []),
    formApi,
    validationId: rValidationUID.current
  };
}
function createFormFieldsValidation(errorIdSuffix, validateField, options = {}) {
  const { message } = options;
  const emittedErrors = /* @__PURE__ */ new Set();
  function createReturnValue(validationResult) {
    if (!validationResult) return;
    const errors = {};
    for (const [key, value] of Object.entries(validationResult)) {
      const fieldName = key;
      const errorMessage = typeof value === "string" && value.trim().length > 0 ? value : message ? message : `The field "${key}" is invalid.`;
      const hasError = !!value;
      const uid = `${key}${errorIdSuffix}`;
      const neverEmittedErrorId = !hasError && !emittedErrors.has(uid);
      if (neverEmittedErrorId) continue;
      emittedErrors.add(uid);
      errors[uid] = hasError ? {
        id: uid,
        field: fieldName,
        message: errorMessage
      } : null;
    }
    return errors;
  }
  return (formApi) => {
    const values = formApi.getValues();
    const result = validateField(values, formApi);
    if (result instanceof Promise) {
      return result.then((result2) => {
        return createReturnValue(result2);
      });
    }
    return createReturnValue(result);
  };
}
export {
  createFormFieldsValidation,
  useAddFormValidation
};
//# sourceMappingURL=useAddFormValidation.js.map
